import ProductMain from '@modules/product-main/product-main.js';
import { gsap } from 'gsap';

class ProductAnimation extends ProductMain {
  constructor() {
    super();

    if (this.dataset.animate == undefined || globalThis.Shopify?.designMode)
      return;
    this.ageVerificationElement = document.querySelector('age-verification');
    this.gsapAnimation = this.gsapAnimation.bind(this); // Bind this to ensure this is the product-animation custom element
  }

  connectedCallback() {
    this.runAnimationOrWaitForAgeVerification();

    // Run the connectedCallback from ProductMain. This has all the event listeners etc for e.g. add to cart and bundle.
    super.connectedCallback();
  }

  /**
   * Runs the animation as and when age verification is complete.
   */
  async runAnimationOrWaitForAgeVerification() {
    if (await this.ageVerificationModalWillShow()) {
      // If the modal will show, then we wait for the success event before running the animation
      this.ageVerificationElement.addEventListener(
        'pxl:ageVerificationSuccessful',
        this.gsapAnimation,
        { once: true }
      );
    } else {
      this.gsapAnimation();
    }
  }

  /**
   * Returns a boolean indicating whether the age verification modal will show.
   * @returns {Promise <boolean>}
   */
  async ageVerificationModalWillShow() {
    // we must first wait for the age verification element to be defined (upgraded) as it is these
    // methods that set the ageIsVerified data attribute
    await globalThis.customElements.whenDefined('age-verification');
    if (!this.ageVerificationElement.dataset.showAgeVerification) return false;
    if (this.ageVerificationElement.ageIsVerified) return false;
    return true;
  }

  /**
   * Animate the product page.
   * @return {void}
   */
  gsapAnimation() {
    try {
      /* We set the starting points for the elements to be animated in product-animation.css
      then use GASP to animation the to() their end points. 
      */
      const imagesTimeline = gsap.timeline({
        callbackScope: this
      });
      const contentTimeline = gsap.timeline({
        paused: true,
        callbackScope: this
      });

      /**
       * The contentTimeline runs after the imagesTimeline is complete. It fades in the product information and form.
       */
      contentTimeline
        .to('[data-product-information], [data-product-form]', {
          opacity: 1,
          duration: 0.7,
          ease: 'power1.inOut'
        })
        .eventCallback('onComplete', () => {
          delete this.dataset.animate;
        });

      /**
       * The imagesTimeline runs as soon as the js is loaded. It changes the opacity of the background image from 1(css) to 0.05,
       * and moves the product image into place.
       */
      if (document.querySelector('[data-product-image]')) {
        imagesTimeline.to(
          '[data-product-image]',
          {
            y: 0,
            duration: 1,
            ease: 'power1.inOut'
          },
          1
        );
      }

      // Conditionally animate background image if it exists
      if (document.querySelector('[data-background-image]')) {
        imagesTimeline.to(
          '[data-background-image]',
          {
            opacity: 0.05,
            duration: 1,
            ease: 'power1.inOut'
          },
          1
        );
      }

      // Check if imagesTimeline has no child tweens (when neither product image nor background image exists)
      if (imagesTimeline.getChildren().length === 0) {
        // If no tweens were added, play contentTimeline immediately
        contentTimeline.play();
      } else {
        // Otherwise, wait for imagesTimeline to complete before playing contentTimeline
        imagesTimeline.eventCallback('onComplete', function () {
          // When the imagesTimeline is complete, we play the contentTimeline.
          contentTimeline.play();
        });
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
      delete this.dataset.animate;
    }
  }
}

customElements.define('product-animation', ProductAnimation);
